import { useMemo, useState, useEffect, useCallback } from "react";
import { api } from "@/lib/firebase/api-client";
import { intervalleVisible } from "@/lib/utils/intervalle-visible";
import { Sparkles } from "lucide-react";
import Loading from "@/components/shared/Loading";
import DemandesFournisseurs from "@/components/DemandesFournisseurs";
import PresenceFournisseurs from "@/components/PresenceFournisseurs";
import EtatMagasins from "@/components/EtatMagasins";
import PerformanceFournisseurs from "@/components/PerformanceFournisseurs";
import PerformanceResponsables from "@/components/PerformanceResponsables";
import {
  useAdminStats,
  useStores,
} from "@/lib/hooks/queries";

export default function AdminDashboard() {
  /**
   * Qui est connecte en ce moment.
   *
   * Connecte = vu il y a moins de deux minutes, soit deux fois l'intervalle
   * du signal. Un seul signal perdu sur un reseau lent ne doit pas faire
   * passer quelqu'un pour parti.
   */
  // Declare ICI, au-dessus des profils, et pas plus bas : le chargement des
  // profils la lit, et une variable lue avant sa declaration est une erreur.
  const [storeFilter, setStoreFilter] = useState<string>("ALL");

  // TOUS les comptes, lus une fois ; le filtre magasin s'applique ici, a
  // l'ecran (01/11/2026). Avant : deux appels (le magasin + la liste
  // complete) toutes les 60 secondes, chacun relisant tous les comptes et
  // toutes les fiches fournisseurs.
  const [tousLesProfils, setTousLesProfils] = useState<any[]>([]);

  /**
   * QUI ON REGARDE : tous, les fournisseurs, ou le support.
   *
   * Remplace l'ancien selecteur de mois. Un mois se choisissait pour des
   * chiffres qui ne sont plus la ; ce qu'on veut savoir en arrivant, c'est
   * qui travaille en ce moment — et la liste complete melange vingt-quatre
   * fournisseurs a trois personnes du support.
   */
  const [filtreRole, setFiltreRole] = useState<"TOUS" | "FOURNISSEUR" | "SUPPORT">(
    "TOUS",
  );

  // Le meme filtre que le serveur (users.handler, GET /) : les comptes sans
  // magasin (direction, support central) restent visibles ; on ne retire
  // que les gens d'un AUTRE magasin. `storeIdEffectif` vient du serveur.
  const profils = useMemo(() => {
    if (storeFilter === "ALL") return tousLesProfils;
    return tousLesProfils.filter(
      (u: any) => !u.storeIdEffectif || u.storeIdEffectif === storeFilter,
    );
  }, [tousLesProfils, storeFilter]);

  // Combien de profils le filtre ecarte. Affiche a cote du titre : un filtre
  // qui agit en silence finit par faire croire a une donnee manquante.
  const profilsMasques =
    storeFilter === "ALL" ? 0 : Math.max(0, tousLesProfils.length - profils.length);

  useEffect(() => {
    let annule = false;
    async function charger() {
      try {
        const tous = await api.users.getAll();
        if (annule) return;
        setTousLesProfils(Array.isArray(tous) ? tous : []);
      } catch {
        /* La liste est un confort : son absence ne casse pas le tableau. */
      }
    }
    void charger();
    // Toutes les 3 minutes, et non plus chaque minute : la pastille « en
    // ligne » tient sur 10 minutes, un rafraichissement de 3 minutes la
    // garde juste. Onglet cache : plus d'appel ; retour : rafraichi aussitot.
    const arret = intervalleVisible(() => void charger(), 3 * 60 * 1000);
    return () => {
      annule = true;
      arret();
    };
  }, []);

  const { data: stats, isLoading: loadingStats } = useAdminStats();
  const { data: stores = [] } = useStores();

  // Les noms, pour le tableau de performance : ceux des profils deja charges
  // et des magasins deja charges — aucune lecture de plus.
  const nomsComptes = useMemo(() => {
    const m = new Map<string, string>();
    for (const p of profils as any[]) m.set(String(p.id), String(p.name || p.username || p.email || p.id));
    return m;
  }, [profils]);
  const nomDuCompte = useCallback((uid: string) => nomsComptes.get(uid) || "—", [nomsComptes]);
  const nomDuMagasin = useCallback(
    (sid: string | null) => (stores as any[]).find((s) => s.id === sid)?.name || "",
    [stores],
  );

  /**
   * Les profils que le filtre laisse passer.
   *
   * « Support » rassemble ceux qui tiennent la boutique de notre cote :
   * support, administrateurs, responsables commande. Le fournisseur est le
   * seul role qui vient du dehors — c'est la coupure qui compte.
   */
  const profilsAffiches = useMemo(() => {
    if (filtreRole === "TOUS") return profils;
    return profils.filter((p: any) => {
      const role = String(p.role || "").toUpperCase();
      return filtreRole === "FOURNISSEUR"
        ? role === "FOURNISSEUR"
        : role !== "FOURNISSEUR";
    });
  }, [profils, filtreRole]);

  if (loadingStats || !stats) {
    return (
      <div className="p-4 md:p-6 min-h-screen flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-slate-50/80 to-gray-100/50">
      <div className="p-3 md:p-8 max-w-7xl mx-auto space-y-5 md:space-y-7">
        {/* Header */}
        <div className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 shadow-xl overflow-hidden border border-white/10">
          <div className="absolute inset-0 opacity-[0.07] pointer-events-none">
            <div
              className="absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                backgroundSize: "36px 36px",
              }}
            />
          </div>
          <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-5">
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-amber-300/90">
                <Sparkles className="w-5 h-5" />
                <span className="text-xs font-semibold uppercase tracking-widest">
                  Pilotage
                </span>
              </div>
              <h1 className="text-2xl md:text-4xl font-bold text-white tracking-tight">
                Dashboard Admin
              </h1>
              <p className="text-sm text-slate-400 max-w-xl">
                Vue mensuelle agrégée (rapide), comparaison au mois précédent,
                classement stores. Données UTC — alignées sur ton back.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 items-stretch sm:items-center">
              {/* TOUS · FOURNISSEURS · SUPPORT.
                  Trois boutons plutot qu'une liste deroulante : le choix est
                  permanent et doit se voir sans ouvrir quoi que ce soit. */}
              <div className="flex items-center rounded-xl bg-white/10 border border-white/15 p-1">
                {(
                  [
                    ["TOUS", "Tous"],
                    ["FOURNISSEUR", "Fournisseurs"],
                    ["SUPPORT", "Support"],
                  ] as const
                ).map(([cle, libelle]) => (
                  <button
                    key={cle}
                    type="button"
                    onClick={() => setFiltreRole(cle)}
                    className={`px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
                      filtreRole === cle
                        ? "bg-white text-slate-900"
                        : "text-white/80 hover:bg-white/10"
                    }`}
                  >
                    {libelle}
                  </button>
                ))}
              </div>

              <select
                value={storeFilter}
                onChange={(e) => setStoreFilter(e.target.value)}
                className="rounded-xl bg-white/10 border border-white/15 text-white text-sm font-medium px-4 py-3 outline-none focus:ring-2 focus:ring-amber-400/50 min-w-[11rem]"
              >
                <option value="ALL" className="text-gray-900">
                  Tous les stores
                </option>
                {stores.map((s: any) => (
                  <option key={s.id} value={s.id} className="text-gray-900">
                    {s.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Les magasins d'abord : un magasin ferme rend tout le reste
            sans objet. Ouvert selon ses horaires, et derniere commande
            recue — en orange quand les deux se contredisent. */}
        <EtatMagasins />

        {/* Qui est connecte. Place juste sous l'en-tete : savoir qui
            travaille conditionne la lecture de tout le reste. */}
        {profilsAffiches.length > 0 && (
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4 md:p-5">
            <div className="flex flex-wrap items-baseline gap-2 mb-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                Profils
              </p>
              {/* Dire lequel des deux on regarde. « Profils » tout court, sur
                  une liste filtree, se lit comme la liste complete — c'est
                  exactement le malentendu qu'on corrige ici. */}
              <span className="text-[11px] font-medium text-gray-400">
                {storeFilter === "ALL"
                  ? "tous les magasins"
                  : `ce magasin${
                      profilsMasques > 0
                        ? ` · ${profilsMasques} profil${
                            profilsMasques > 1 ? "s" : ""
                          } d'un autre magasin masqué${
                            profilsMasques > 1 ? "s" : ""
                          }`
                        : ""
                    }`}
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {profilsAffiches.map((p: any) => {
                const vu = p.lastSeenAt
                  ? new Date(
                      p.lastSeenAt?._seconds
                        ? p.lastSeenAt._seconds * 1000
                        : p.lastSeenAt,
                    )
                  : null;
                const minutes = vu
                  ? Math.floor((Date.now() - vu.getTime()) / 60000)
                  : null;
                // DIX MINUTES, ET NON DEUX.
                //
                // Le battement part toutes les minutes, mais un telephone
                // pose sur le comptoir met son navigateur en veille : les
                // minuteries s'arretent et le battement ne part plus. Avec
                // un seuil de deux minutes, des fournisseurs en train de
                // preparer une commande s'affichaient tous en rouge — un
                // ecran qui crie au loup en permanence ne se lit plus.
                //
                // Dix minutes laissent passer une veille d'ecran sans
                // laisser passer une vraie absence.
                const enLigne = minutes !== null && minutes < 10;

                return (
                  <div
                    key={p.id}
                    className={`flex items-center gap-2 rounded-xl border px-3 py-2 ${
                      enLigne
                        ? "border-green-200 bg-green-50"
                        : vu
                          ? "border-red-200 bg-red-50"
                          : "border-gray-200 bg-gray-50"
                    }`}
                  >
                    <span
                      className={`inline-block w-2.5 h-2.5 rounded-full ${
                        enLigne ? "bg-green-500" : vu ? "bg-red-500" : "bg-gray-300"
                      }`}
                    />
                    <div className="leading-tight">
                      <p className="text-sm font-bold text-gray-900">
                        {p.name || p.username || p.email || p.id}
                      </p>
                      <p
                        className={`text-[11px] font-medium ${
                          enLigne
                            ? "text-green-700"
                            : vu
                              ? "text-red-700"
                              : "text-gray-400"
                        }`}
                      >
                        {/* Un compte jamais vu est GRIS, pas rouge : le rouge
                            veut dire « parti », et ne doit pas se confondre
                            avec « n'est jamais venu ». */}
                        {enLigne
                          ? "connecté"
                          : vu
                            ? minutes !== null && minutes < 60
                              ? `vu il y a ${minutes} min`
                              : `vu le ${vu.toLocaleDateString("fr-FR")}`
                            : "jamais connecté"}
                        {p.role ? ` · ${String(p.role).toLowerCase()}` : ""}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Ce que les fournisseurs ont change eux-memes — et, en rouge
            clignotant, les fermetures de service qu'il faut trancher.
            Le bloc vit dans son propre fichier : le support voit
            exactement le meme, avec le meme son. */}
        <DemandesFournisseurs storeId={storeFilter} />

        {/* « IL EST OUVERT » (29/10/2026) : l'admin a la meme main que le
            support et le responsable commande — remettre en vente les
            articles d'un fournisseur sur Glovo, Kooul et le site. Le
            magasin choisi en haut filtre la liste. */}
        <PresenceFournisseurs storeId={storeFilter} />

        {/* LA VUE D'ENSEMBLE DES FOURNISSEURS (13/10/2026) : preparation, CA,
            catalogue, pertes — une ligne chacun, pour decider. Les cartes
            Profils au-dessus ne disent plus que qui est la. */}
        <PerformanceFournisseurs
          storeFilter={storeFilter}
          nomDuCompte={nomDuCompte}
          nomDuMagasin={nomDuMagasin}
        />

        {/* LES RESPONSABLES COMMANDE (15/10/2026) : qui a traite quoi, en
            combien de temps, avec combien d'erreurs. */}
        <PerformanceResponsables storeFilter={storeFilter} nomDuMagasin={nomDuMagasin} />

        {/* LE BAS DU TABLEAU DE BORD A ETE RETIRE.
            Les tuiles mensuelles, l'acces rapide et l'etat des commandes
            vivaient ici. Ils sont retires en attendant de savoir ce que
            cet ecran doit vraiment montrer : un tableau de bord qu'on ne
            regarde pas est un tableau de bord qui ment, parce qu'on finit
            par ne plus verifier ses chiffres. */}
      </div>
    </div>
  );
}
